import {
    Stack,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"

import { KnowledgeItemStore } from "src/modals/knowledge-items/store"
import { useStore } from "src/store/lib/useStore"
import { normalizeUrl } from "src/lib/urlLinksHandler"

export const KnowledgeItemsWebSearch = observer(() => {
    const store = useStore(KnowledgeItemStore)
    const url = store.formFields.get("content") as string
    const searchInstructions = store.formFields.get(
        "search_instructions",
    ) as string
    const isFullSiteSearch = store.formFields.get(
        "is_full_site_search",
    ) as boolean
    const accessType = store.formFields.get("access_type")
    const contentError = store.formFields.error("content")
    const searchInstructionsError = store.formFields.error(
        "search_instructions",
    )

    const handleUrlChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = event.target.value
            const normalizedUrl = normalizeUrl(inputValue)
            store.formFields.set("content", normalizedUrl)
        },
        [store.formFields],
    )

    const handleSearchInstructionsChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            store.formFields.set("search_instructions", event.target.value)
        },
        [store.formFields],
    )

    const handleSearchScopeChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            store.formFields.set("is_full_site_search", event.target.checked)
        },
        [store.formFields],
    )

    const isDisabled = useMemo(() => accessType === "READ", [accessType])
    const hasContentError = useMemo(() => Boolean(contentError), [contentError])
    const hasSearchInstructionsError = useMemo(
        () => Boolean(searchInstructionsError),
        [searchInstructionsError],
    )

    return (
        <Stack spacing={3}>
            <TextField
                label="URL"
                onChange={handleUrlChange}
                value={url ?? ""}
                helperText={contentError ?? "Enter the website URL to search"}
                error={hasContentError}
                disabled={isDisabled}
                placeholder="https://www.avy.se"
                inputProps={{
                    maxLength: 2048,
                    type: "url",
                }}
                fullWidth
            />

            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Search Scope
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isFullSiteSearch ?? false}
                            onChange={handleSearchScopeChange}
                            disabled={isDisabled}
                        />
                    }
                    label="Full site search"
                />
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                >
                    {isFullSiteSearch
                        ? "Search the entire website including all subpages"
                        : "Search only the specific page provided"}
                </Typography>
            </Box>

            <TextField
                label="Search Instructions"
                onChange={handleSearchInstructionsChange}
                value={searchInstructions ?? ""}
                helperText={
                    searchInstructionsError ??
                    "Provide specific instructions for how the AI should search and interpret content from this website"
                }
                error={hasSearchInstructionsError}
                disabled={isDisabled}
                placeholder="Example: As a landlord, avoid referring to 'My pages' and instead use 'Your account' or 'Customer portal'"
                multiline
                rows={4}
                fullWidth
            />
        </Stack>
    )
})
